import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Check,
  X,
  FileText,
  User,
  Phone,
  Mail,
  Calendar,
  MapPin,
  ZoomIn,
  Shield,
  Clock,
  CreditCard,
  Star,
  ArrowLeft,
  Car,
} from "lucide-react";
import { formatDate } from "@/lib/utils";
import { vehicleCapacityLabel } from "@/constants/vehicles";
import { ROUTES } from "@/constants/routes";
import {
  usePendingKYC,
  useApproveDriver,
  useRejectDriver,
} from "../hooks/useDrivers";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from "@/components/ui/modal";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageSpinner } from "@/components/ui/spinner";
import { EmptyState } from "@/components/ui/empty-state";
import type { PendingDriverAPIResponse } from "@/types";

function VerificationBadge({ status }: { status: string }) {
  const variant =
    status === "approved"
      ? "success"
      : status === "rejected"
        ? "destructive"
        : "warning";
  return (
    <Badge variant={variant} className="text-xs font-semibold">
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number | null | undefined;
}) {
  return (
    <div className="flex items-start gap-3 py-3.5 border-b border-border/40 last:border-0">
      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
        <Icon className="h-4.5 w-4.5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {label}
        </p>
        <p className="text-sm font-semibold mt-0.5 truncate">{value || "—"}</p>
      </div>
    </div>
  );
}

function DocumentCard({
  title,
  imageUrl,
  onPreview,
}: {
  title: string;
  imageUrl: string;
  onPreview: () => void;
}) {
  return (
    <div className="group relative rounded-xl overflow-hidden border border-border bg-card shadow-sm hover:shadow-lg transition-all duration-300">
      <div
        className="relative h-48 bg-muted cursor-pointer overflow-hidden"
        onClick={onPreview}
      >
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              `https://placehold.co/400x250/1e293b/94a3b8?text=${encodeURIComponent(title)}`;
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end justify-center pb-4">
          <div className="flex items-center gap-1.5 text-white text-sm font-medium bg-black/30 px-3 py-1.5 rounded-full backdrop-blur-sm">
            <ZoomIn className="h-4 w-4" />
            View Full Size
          </div>
        </div>
      </div>
      <div className="p-3.5 flex items-center gap-2">
        <FileText className="h-4 w-4 text-primary" />
        <p className="font-semibold text-sm">{title}</p>
      </div>
    </div>
  );
}

export function KYCDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const { data: pendingDrivers, isLoading } = usePendingKYC();
  const { mutate: approve, isPending: approving } = useApproveDriver();
  const { mutate: reject, isPending: rejecting } = useRejectDriver();

  if (isLoading) {
    return <PageSpinner message="Loading driver details..." />;
  }

  const driver = pendingDrivers?.find(
    (d: PendingDriverAPIResponse) => d.id === id,
  );

  if (!driver) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Driver Not Found"
          breadcrumbs={[
            { label: "Drivers", href: ROUTES.DRIVERS },
            { label: "Pending KYC", href: ROUTES.KYC_PENDING },
            { label: "Not Found" },
          ]}
        />
        <EmptyState
          icon="default"
          title="Driver not found"
          description="The driver KYC record you're looking for doesn't exist or has been processed."
          action={{
            label: "Back to Pending KYC",
            onClick: () => navigate(ROUTES.KYC_PENDING),
          }}
        />
      </div>
    );
  }

  const handleApprove = () => {
    approve(driver.id, {
      onSuccess: () => navigate(ROUTES.KYC_PENDING),
    });
  };

  const handleReject = () => {
    if (rejectReason) {
      reject(
        { id: driver.id, reason: rejectReason },
        {
          onSuccess: () => {
            setRejectModal(false);
            setRejectReason("");
            navigate(ROUTES.KYC_PENDING);
          },
        },
      );
    }
  };

  const documents = [
    { title: "Aadhaar Front", url: driver.aadhar_front_image },
    { title: "Aadhaar Back", url: driver.aadhar_back_image },
    { title: "Driving License", url: driver.driving_license_image },
    { title: "Vehicle RC", url: driver.vehicle_rc_image },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="KYC Verification"
        description={`Review KYC for ${driver.user.name}`}
        breadcrumbs={[
          { label: "Drivers", href: ROUTES.DRIVERS },
          { label: "Pending KYC", href: ROUTES.KYC_PENDING },
          { label: driver.user.name },
        ]}
        actions={
          <Button
            variant="outline"
            onClick={() => navigate(ROUTES.KYC_PENDING)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
        }
      />

      {/* Driver Header Card */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl overflow-hidden border-2 border-background shadow-lg">
                <img
                  src={driver.profile_image}
                  alt={driver.user.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-warning border-2 border-background flex items-center justify-center">
                <Clock className="h-3.5 w-3.5 text-warning-foreground" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold">{driver.user.name}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {driver.user.email}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <VerificationBadge status={driver.verification_status} />
                <Badge variant="outline" className="capitalize">
                  {driver.availability_status}
                </Badge>
                {driver.user.is_verified && (
                  <Badge variant="success" className="text-xs">
                    <Check className="h-3 w-3 mr-1" /> Phone Verified
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex gap-3 w-full sm:w-auto">
              <Button
                variant="destructive"
                size="lg"
                className="flex-1 sm:flex-initial shadow-sm"
                onClick={() => setRejectModal(true)}
                disabled={approving}
              >
                <X className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button
                variant="success"
                size="lg"
                className="flex-1 sm:flex-initial shadow-sm"
                onClick={handleApprove}
                isLoading={approving}
              >
                <Check className="h-4 w-4 mr-2" />
                Approve
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-5 w-5 text-primary" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <InfoRow icon={User} label="Full Name" value={driver.user.name} />
            <InfoRow icon={Phone} label="Phone" value={driver.user.phone} />
            <InfoRow icon={Mail} label="Email" value={driver.user.email} />
            <InfoRow icon={MapPin} label="Address" value={driver.address} />
          </CardContent>
        </Card>

        {/* License & Account */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="h-5 w-5 text-primary" />
              License & Account
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <InfoRow
              icon={Shield}
              label="License Number"
              value={driver.license_number}
            />
            <InfoRow
              icon={Calendar}
              label="License Expiry"
              value={formatDate(driver.license_expiry)}
            />
            <InfoRow
              icon={CreditCard}
              label="Wallet Balance"
              value={`₹${driver.wallet_balance}`}
            />
            <InfoRow
              icon={Star}
              label="Rating"
              value={driver.average_rating ?? "No ratings yet"}
            />
          </CardContent>
        </Card>

        {/* Vehicle Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Car className="h-5 w-5 text-primary" />
              Vehicle Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {driver.vehicle ? (
              <>
                <InfoRow
                  icon={Car}
                  label="Type"
                  value={driver.vehicle.vehicle_type ? String(driver.vehicle.vehicle_type).replace(/_/g, " ") : "—"}
                />
                <InfoRow
                  icon={FileText}
                  label="Registration No."
                  value={driver.vehicle.registration_number || "—"}
                />
                <InfoRow icon={Car} label="Model" value={driver.vehicle.vehicle_model || "—"} />
                <InfoRow
                  icon={Shield}
                  label="Capacity"
                  value={vehicleCapacityLabel(driver.vehicle.vehicle_type)}
                />
              </>
            ) : (
              <p className="text-sm text-muted-foreground py-2">No vehicle registered yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Account Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-5 w-5 text-primary" />
            Account Timeline
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid gap-0 md:grid-cols-2">
            <InfoRow
              icon={Calendar}
              label="Account Created"
              value={formatDate(driver.user.created_at)}
            />
            <InfoRow
              icon={Clock}
              label="Last Updated"
              value={formatDate(driver.user.updated_at)}
            />
            <InfoRow
              icon={Calendar}
              label="KYC Submitted"
              value={formatDate(driver.created_at)}
            />
            <InfoRow
              icon={Check}
              label="Phone Verified"
              value={driver.user.is_verified ? "Yes" : "No"}
            />
          </div>
        </CardContent>
      </Card>

      {/* Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5 text-primary" />
            Uploaded Documents
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {documents.map((doc) => (
              <DocumentCard
                key={doc.title}
                title={doc.title}
                imageUrl={doc.url}
                onPreview={() => setImagePreview(doc.url)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Profile Photo */}
      {driver.profile_image && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-5 w-5 text-primary" />
              Profile Photo
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="max-w-xs">
              <DocumentCard
                title="Profile Photo"
                imageUrl={driver.profile_image}
                onPreview={() => setImagePreview(driver.profile_image)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bottom Action Bar */}
      <Card className="sticky bottom-4 shadow-lg border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Avatar
                src={driver.profile_image}
                alt={driver.user.name}
                size="sm"
              />
              <div>
                <p className="font-semibold text-sm">{driver.user.name}</p>
                <p className="text-xs text-muted-foreground">
                  {driver.verification_status} verification
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                onClick={() => setRejectModal(true)}
                disabled={approving}
              >
                <X className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button
                variant="success"
                onClick={handleApprove}
                isLoading={approving}
              >
                <Check className="h-4 w-4 mr-2" />
                Approve
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reject Modal */}
      <Modal isOpen={rejectModal} onClose={() => setRejectModal(false)}>
        <ModalHeader onClose={() => setRejectModal(false)}>
          <ModalTitle>Reject KYC</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Please provide a reason for rejecting{" "}
              <strong>{driver.user.name}</strong>&apos;s KYC application. The
              driver will be notified.
            </p>
            <div className="space-y-2">
              <Label required>Rejection Reason</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={4}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setRejectModal(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleReject}
            isLoading={rejecting}
            disabled={!rejectReason.trim()}
          >
            Reject KYC
          </Button>
        </ModalFooter>
      </Modal>

      {/* Image Preview Modal */}
      <Modal isOpen={!!imagePreview} onClose={() => setImagePreview(null)}>
        <ModalHeader onClose={() => setImagePreview(null)}>
          <ModalTitle>Document Preview</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Document"
              className="w-full rounded-lg"
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "https://placehold.co/600x400/1e293b/94a3b8?text=Image+Not+Available";
              }}
            />
          )}
        </ModalBody>
      </Modal>
    </div>
  );
}
